import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { NotaFiscal, ItemNotaFiscal } from '../../types';
import { ArrowLeftIcon, CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';

const NotaFiscalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [nota, setNota] = useState<NotaFiscal | null>(null);
  const [itens, setItens] = useState<ItemNotaFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadNota();
    }
  }, [id]);

  const loadNota = async () => {
    try {
      setLoading(true);
      const response = await estoqueApi.getNotaFiscal(Number(id));
      setNota(response);
      setItens(response.itens || []);
    } catch (err: any) {
      setError('Erro ao carregar nota fiscal');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLancar = async () => {
    if (!nota) return;

    if (!window.confirm('Tem certeza que deseja lançar esta nota no estoque?')) {
      return;
    }

    try {
      await estoqueApi.lancarNotaFiscal(Number(nota.id));
      alert('Nota fiscal lançada no estoque com sucesso!');
      navigate('/estoque/notas-fiscais');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao lançar nota fiscal');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  if (error || !nota) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error || 'Nota fiscal não encontrada'}
      </div>
    );
  }

  const itensMapeados = itens.filter(item => item.mapeado_automaticamente).length;
  const itensNaoMapeados = itens.length - itensMapeados;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/estoque/notas-fiscais')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-base font-bold text-gray-900">
              NF-e {nota.numero_nota}/{nota.serie}
            </h1>
            <p className="text-xs text-gray-600">{nota.emitente_razao_social}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          {nota.status === 'PENDENTE' && (
            <button
              onClick={() => navigate(`/estoque/notas-fiscais/${id}/conferir`)}
              className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1.5" />
              Conferir
            </button>
          )}
          {nota.status === 'CONFERIDA' && (
            <button
              onClick={handleLancar}
              className="flex items-center px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
            >
              <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
              Lançar no Estoque
            </button>
          )}
        </div>
      </div>

      {/* Informações da Nota */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Informações da Nota</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-600">Data de Emissão</p>
            <p className="text-sm font-medium text-gray-900">{formatDate(nota.data_emissao)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Valor Total</p>
            <p className="text-sm font-medium text-gray-900">{formatCurrency(nota.valor_total)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Status</p>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              nota.status === 'LANCADA' ? 'bg-green-100 text-green-800' :
              nota.status === 'CONFERIDA' ? 'bg-blue-100 text-blue-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {nota.status_display}
            </span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-gray-600">Chave de Acesso</p>
          <p className="text-xs font-mono text-gray-900">{nota.chave_acesso}</p>
        </div>
      </div>

      {/* Resumo de Itens */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Resumo de Itens</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded">
            <p className="text-2xl font-bold text-blue-600">{itens.length}</p>
            <p className="text-xs text-gray-600">Total de Itens</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded">
            <p className="text-2xl font-bold text-green-600">{itensMapeados}</p>
            <p className="text-xs text-gray-600">Mapeados</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded">
            <p className="text-2xl font-bold text-yellow-600">{itensNaoMapeados}</p>
            <p className="text-xs text-gray-600">Não Mapeados</p>
          </div>
        </div>
      </div>

      {/* Lista de Itens */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Qtd</th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Valor Unit.</th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Material</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {itens.map((item) => (
              <tr key={item.id} className="hover:bg-gray-25">
                <td className="px-2 py-1.5 text-xs text-gray-900">{item.numero_item}</td>
                <td className="px-2 py-1.5 text-xs text-gray-900">{item.codigo_produto}</td>
                <td className="px-2 py-1.5 text-xs text-gray-900">{item.descricao}</td>
                <td className="px-2 py-1.5 text-xs text-center text-gray-900">
                  {item.quantidade} {item.unidade}
                </td>
                <td className="px-2 py-1.5 text-xs text-right text-gray-900">
                  {formatCurrency(item.valor_unitario)}
                </td>
                <td className="px-2 py-1.5 text-xs text-right font-semibold text-gray-900">
                  {formatCurrency(item.valor_total)}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {item.material ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ {item.material_nome}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Não mapeado
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NotaFiscalDetail;
